import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch, useHistory } from 'react-router-dom'
import { useLocation } from 'react-router'
import queryString from 'query-string'
import ReactPlayer from 'react-player'
import clsx from 'clsx'
import { addMonths, endOfMonth, eachMonthOfInterval, format } from 'date-fns'
import startOfMonth from 'date-fns/startOfMonth'
import pt from 'date-fns/locale/pt'

import {
  Typography,
  useDimensions,
  Spinner,
  Button,
  Select,
} from '@telavita-core/react-design-kit'

import ReservationService from 'services/reservation'
import { getProfessionalAvailability } from 'services/professionalAvailabilityService'
import { getCorporateEligibility } from 'services/corporateEligibilityService'
import ProfessionalsService from 'services/professionals'

import { creditsPanelRequest } from 'redux/modules/creditsPanel/requests'
import { createOrder, saveOrder } from 'redux/actions/ordersActions'

import ScheduleContainer from 'containers/ScheduleContainer'
import { RequiredPatientInfoModal } from 'containers/ModalManager/Modals/RequiredPatientInfoModal'
import {
  therapyApproachesList,
  TherapyApproachModal,
} from 'containers/ModalManager/Modals/TherapyApproachModal'

import ProfileEducation from 'components/ProfileEducation'
import Row from 'components/Row'
import ProfileSpecialities from 'components/ProfileSpecialities'
import Footer from 'components/Footer'
import { ListContainer, Review, ReviewLoading } from 'components/Review'
import {
  SocialLinks,
  Container,
  Column,
  CreditsPanel,
  Breadcrumb,
  ProfessionalInfo,
  LineBreaker,
  TextContainer,
  CoreIcons,
} from 'components'

import BallonCommentIcon from 'components/CoreIcons/Icons/BallonComment'
import { ReactComponent as KidsIcon } from '../../static/svg/kids_icon.svg'
import { ReactComponent as ElderlysIcon } from '../../static/svg/elderlys_icon.svg'
import { ReactComponent as AdultsIcon } from '../../static/svg/adults_icon.svg'
import { ReactComponent as AdolescentsIcon } from '../../static/svg/adolescents_icon.svg'
import { ReactComponent as SignLanguageFlag } from '../../static/svg/sign-language-flag.svg'

import PortugueseFlagImage from 'static/images/portuguese-flag-large.png'
import EnglishFlagImage from 'static/images/english-flag-large.png'
import SpanishFlagImage from 'static/images/spanish-flag-large.png'
import GermanFlagImage from 'static/images/german-flag-large.png'

import { getProfessionalCodeByURL } from 'utils/profileCode'

import axios from 'settings/axios'
import * as routes from 'settings/_routesSettings'
import { apiEndpoints } from 'settings/_apiSettings'
import { PlanGroupCategoryEnum } from 'settings/_planGroupSettings'
import { reviewsOptions } from 'settings/_reviewsOptionsSettings'
import { DISMISSED_PATIENT_REQUIRE_INFO_MODAL } from 'settings/_storageFlagsSettings'
import { usePatientEligibility } from 'hooks/usePatientEligibility'
import { TVTVPACI, TVTVPSIC, TVTVPSIQ } from 'settings/_profileSettings'
import { FROM_PROFESSIONAL_PROFILE } from 'settings/_scheduledFrom'

import { INELIGIBLE } from 'settings/_patientTreatmentStatus'
import UpdateDefaultPlanModal from 'components/UpdateDefaultPlanModal'
import { getPatientPlans } from 'services/patients/getPatientPlans'
import { INACTIVE } from 'settings/_personSettings'
import { updatePatientDefaultPlan } from 'services/updatePatientDefaultPlan'

import { ReservationRecurrence } from 'models/Reservation'

import { modalTypes } from 'containers/ModalManager/ModalManager'
import { showModal, hideModal } from 'redux/actions'
import { usePersonGeneralData, useUpdateProfileSelectedPlansLocally, useEnablePsychiatry, useAllowsAutomaticSchedulingThroughReservation } from 'hooks'

/* Agora o paciente com plano enterprise que tiver uma reserva recorrente ou uma reserva recorrente + avulsa,
vai ter a mesma frequência de agendamento de um corporate semanal */
const FREQUENCY_OF_ENTERPRISE_WITH_RESERVATION = '7 days, 0:00:00'

const ProfessionalProfile = () => {
  const location = useLocation()
  const videoDiv = useRef(null)
  const refReviewsEl = useRef(null)
  const routeMatch = useRouteMatch()
  const dispatch = useDispatch()
  const { isMobile } = useDimensions()
  const history = useHistory()
  const fromMonth = startOfMonth(new Date())
  const toMonth = endOfMonth(addMonths(new Date(), 3))

  const [loading, setLoading] = useState(true)
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [professional, setProfessional] = useState(null)
  const [reservations, setReservations] = useState([])

  const [maxSchedules, setMaxSchedules] = useState(null)

  const [
    fetchedAvailabilitiesMonths,
    setFetchedAvailabilitiesMonths,
  ] = useState([])

  const [loadingAvailabilities, setLoadingAvailabilities] = useState(true)
  const [availabilities, setAvailabilities] = useState([])
  const [schedules, setSchedules] = useState([])
  const [error, setError] = useState(null)
  const [playVideo, setPlayVideo] = useState(false)
  const [showLayer, setShowLayer] = useState(false)
  const [reviews, setReviews] = useState([])
  const [loadingReviews, setLoadingReviews] = useState(true)
  const [totalReviews, setTotalReviews] = useState(0)
  const [pageReviews, setPageReviews] = useState(true)
  const [
    showRequiredPatientInfoModal,
    setShowRequiredPatientInfoModal,
  ] = useState(false)
  const [
    isPatientAlreadyHasRequiredInfo,
    setIsPatientAlreadyHasRequiredInfo,
  ] = useState(false)
  const [showTherapyApproachModal, setShowTherapyApproachModal] = useState(
    false
  )
  const [showUpdateDefaultPlanModal, setShowUpdateDefaultPlanModal] = useState(false)
  const [patientPlans, setPatientPlans] = useState([])

  const {
    hasButton,
    setHasButton,
    planGroupName,
    creditsStatus,
    maxCredits,
    enableDetails,
    validDate,
    expired,
    errorButton,
    loadingButton,
    solicitationDate,
    isUnlimited,
    startDate,
    corporateEligibility,
    loading: loadingEligibility,
    forceUpdate,
    psychiatryEligibilityLoading,
    psychiatryEligibility,
    isCareplus,
    isCassi,
    isCapitation,
    planGroupCode,
    onClickRequestCreditsButton,
    handleRequestTreatementStatus,
    handleUpdateSelectedPlan,
    reasonCode,
    creditsPanelErrorMessage,
    patientPlans: patientPlansEligibility,
  } = usePatientEligibility(true)

  const { hasAddressData, hasRelativeData } = usePersonGeneralData()
  const { updateProfileSelectedPlansLocally } = useUpdateProfileSelectedPlansLocally()

  const showPsychiatry = useEnablePsychiatry()

  const [selectedReviewOption, setSelectedReviewOption] = useState(() =>
    reviewsOptions.find((review) => review.id === 1)
  )

  const {
    ENTERPRISE,
    CARE_PLUS,
    CORPORATE_LIMITED,
    CORPORATE_UNLIMITED,
    PARTICULAR,
    CAPITATION
  } = PlanGroupCategoryEnum

  const isAuth = useSelector((state) => state.auth.isAuth)
  const planGroupCategory = useSelector(
    (state) => state.auth.planSelected?.plan_group.category
  )
  const userData = useSelector((state) => state.auth.userData)
  const userProfileCode = userData?.person_profile[0]?.profile_code
  const username = useSelector((state) => state.auth.userData?.username)
  const planCode = useSelector(
    (state) => state.auth.planSelected?.partner_plan_code
  )
  const isParticularPlan = useSelector(
    (state) => state.auth.planSelected?.partner_plan_is_particular
  )

  const frequency = useSelector(
    (state) => state.auth.planSelected?.plan_group.frequency
  )

  const isCorporate = [CORPORATE_LIMITED, CORPORATE_UNLIMITED].includes(
    planGroupCategory
  )
  const isCorporateUnlimited = CORPORATE_UNLIMITED.includes(planGroupCategory)
  const isEnterprise = useSelector((state) =>
    [ENTERPRISE, CARE_PLUS, CAPITATION].includes(
      state.auth.planSelected?.plan_group.category
    )
  )
  const creditsPanel = useSelector((state) => state.creditsPanel.data)
  const availableCredits = creditsPanel.available_credits
  const profileCode = professional?.person_profile[0].profile_code
  const ageRanges = professional?.person_profile[0].age_ranges
  const languages = professional?.person_profile[0].languages
  const isPsychiatrist = profileCode === TVTVPSIQ
  const isPsychologist = profileCode === TVTVPSIC
  const hasVideo = professional?.person_general.video_url
  const { friendlyUrl } = routeMatch.params
  const profileRole = getProfessionalCodeByURL(window.location.pathname)
  const isLoggedUserPatient = userProfileCode === TVTVPACI

  const hideCreditsPanel = !(isAuth && (isCorporate || isEnterprise))

  const userDataAuthenticated = userData
    ? {
      cpf: userData?.person_general?.cpf,
      dateBirth: userData?.person_general?.date_birth,
      gender: userData?.person_general?.gender,
    }
    : null

  const videoClasses = clsx({
    ProfessionalProfile__video: true,
  })

  const orderedPatientPlans = patientPlans.sort((a, b) => {
    const aPlanGroupName = a.planGroupName.toLowerCase()
    const bplanGroupName = b.planGroupName.toLowerCase()

    if (a.isParticular && !b.isParticular) {
      return 1
    }
    if (!a.isParticular && b.isParticular) {
      return -1
    }
    if (aPlanGroupName < bplanGroupName) {
      return -1
    }
    if (aPlanGroupName > bplanGroupName) {
      return 1
    }
    return 0
  })

  useEffect(() => {
    if (isAuth && isLoggedUserPatient) {
      updateProfileSelectedPlansLocally()
    }
  }, [])

  useEffect(() => {
    if (isAuth) {
      getPatientPlans({
        username,
        professionalSlug: friendlyUrl
      }).then(response => {
        const activePlans = response.plans.reduce((acc, curr) => {
          if (curr.status !== INACTIVE) {
            acc.push({
              planGroupName: curr.plan_group.name,
              planName: curr.name,
              isEnable: curr.attended_by_professional,
              planCode: curr.plan_code,
              isParticular: curr.is_particular
            })
          }
          return acc
        },[])
        setPatientPlans(activePlans)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isAuth])

  useEffect(() => {
    if (userDataAuthenticated) {
      const { cpf, dateBirth, gender } = userDataAuthenticated
      const patientHasRequiredInfo = cpf && dateBirth && gender

      if (patientHasRequiredInfo) {
        setIsPatientAlreadyHasRequiredInfo(true)
      } else {
        setIsPatientAlreadyHasRequiredInfo(false)
      }
    }
  }, [userDataAuthenticated])

  useEffect(() => {
    axios
      .get(apiEndpoints.PROFESSIONAL_DETAIL(friendlyUrl))
      .then((response) => {
        if (!response.data.person_profile[0].products[0]) {
          setError(406)
        }
        setProfessional(response.data)
        setLoading(false)
      })
      .catch((error) => {
        if (error.response?.status === 404) history.push('/404')
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isCorporate) {
      getCorporateEligibility({ username, planCode }).then((res) => {
        if (isCorporateUnlimited) return

        setMaxSchedules(res.availableCredits)
      })
    } else if (isEnterprise) {
      dispatch(creditsPanelRequest())
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planGroupCategory])

  useEffect(() => {
    if (!professional) return

    const productCode =
      professional?.person_profile[0]?.products[0]?.product.code
    const isParticular = userData?.person_profile[0].plans.find(
      (plan) => plan.plan_group.category === PARTICULAR
    )

    const { availabilities } = queryString.parse(location.search)

    if (availabilities && isParticular) {
      handleSubmit(JSON.parse(availabilities))
    } else {
      getAvailabilities(friendlyUrl, productCode, profileRole)
    }

    async function getReviews() {
      const {
        reviews,
        pagination,
      } = await ProfessionalsService.listProfessionalReviews({
        professionalUUID: professional.uuid,
      })

      setReviews(reviews)
      setPageReviews(pagination.page)
      setTotalReviews(pagination.total)
      setLoadingReviews(false)
    }

    getReviews()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professional])

  useEffect(() => {
    if (!username || !professional || isPsychiatrist) return

    ReservationService.getReservations({
      username: username,
      isProfessional: false,
    })
      .then((reservations) => {
        setReservations(reservations)
      })
      .catch((error) => {
        console.error(error)
      })
  }, [username, professional, isPsychiatrist])

  const handleUpdateDefaultPlan = async (planCode) => {
    setShowUpdateDefaultPlanModal(false)

    handleUpdateSelectedPlan(planCode)

    await updatePatientDefaultPlan({
      planCode: planCode,
      username: username
    })

    const response = await axios.get(apiEndpoints.PROFESSIONAL_DETAIL(friendlyUrl))

    setProfessional(response.data)

    setLoadingAvailabilities(true)
    setSchedules([])
    setAvailabilities([])
    setError(null)
  }

  async function getAvailabilities(slug, productCode, profileRole) {
    if (!productCode) {
      setLoadingAvailabilities(false)
      return
    }

    const interval = eachMonthOfInterval({ start: fromMonth, end: toMonth })

    for await (let month of interval) {
      await getProfessionalAvailability({
        slug: slug,
        startDate: startOfMonth(month).toISOString(),
        endDate: endOfMonth(month).toISOString(),
        planCode: planCode,
        productCode: productCode,
        groupBy: 'date',
        username: isPsychologist || isPsychiatrist && isParticularPlan ? username : null,
        patientUsername: isPsychiatrist && !isParticularPlan ? username : null,
        profileRole: profileRole,
        isParticular: !isAuth ? true : isParticularPlan
      })
        .then(({ availabilities, schedules }) => {
          setFetchedAvailabilitiesMonths((prev) => [...prev, month.getMonth()])
          setLoadingAvailabilities(false)
          setAvailabilities((prev) => [...prev, ...availabilities])
          setSchedules((prev) => [...prev, ...schedules])
        })
        .catch((error) => {
          setLoadingAvailabilities(false)
          setAvailabilities([])
          setError(error)
        })
    }
  }

  const handleOpenPatientInfoModal = () => {
    setShowRequiredPatientInfoModal(true)
  }

  const handleCloseRequiredPatientInfoModal = () => {
    sessionStorage.setItem(DISMISSED_PATIENT_REQUIRE_INFO_MODAL, true)
    setShowRequiredPatientInfoModal(false)
  }

  const handleSubmit = async (availabilities) => {
    if (!isAuth) {
      const redirectUrl = encodeURI(
        `${routeMatch.url}?availabilities=${JSON.stringify(availabilities)}`
      )
      return history.push(`${routes.LOGIN}?redirectTo=${redirectUrl}`)
    }

    setLoadingSubmit(true)

    if (isPsychiatrist) {
      if (psychiatryEligibility === INELIGIBLE) {
        setLoadingSubmit(false)
        setSchedules([])
        setAvailabilities([])
        return
      }
    }

    const payload = {
      product_group_code: professional?.person_profile[0].products[0].product.product_group.code,
      plan_code: planCode,
      profile_role: getProfessionalCodeByURL(window.location.pathname),
      person_profile_product_appointment_id: professional?.person_profile[0].products[0].appointments[0].id,
      scheduledFrom: FROM_PROFESSIONAL_PROFILE,
      schedules: availabilities.map(availability => {
        return {
          start_date: availability.startDate,
          end_date: availability.endDate,
          guests: [
            {
              person_profile_id : professional?.person_profile[0].id
            },
            {
              person_profile_id : userData.person_profile[0].id
            }
          ]
        }
      })
    }

    await dispatch(saveOrder(payload))
    await dispatch(createOrder(payload, true))

    setLoadingSubmit(false)
  }

  function handleCreateNewSchedule(availabilities) {
    const shouldShowAddressAndFamilyContactModal = isAuth && isPsychiatrist && (!hasAddressData || !hasRelativeData)
    if (shouldShowAddressAndFamilyContactModal) {
      const modalData = {
        onSuccess: () => {
          dispatch(hideModal())
          handleSubmit(availabilities)
        }
      }
      return dispatch(showModal(modalTypes.ADDRESS_AND_FAMILY_CONTACT, modalData))
    }

    return handleSubmit(availabilities)
  }

  const onMonthChange = (date) => {
    if (!fetchedAvailabilitiesMonths.includes(date.getMonth()))
      setLoadingAvailabilities(true)
  }

  const handleSelectReviewOption = async (optionId) => {
    setLoadingReviews(true)
    setReviews([])

    const reviewOption = reviewsOptions.find(
      (option) => option.id === optionId
    )
    setSelectedReviewOption(reviewOption)

    const {
      reviews,
      pagination,
    } = await ProfessionalsService.listProfessionalReviews({
      professionalUUID: professional.uuid,
      viewAll: optionId === 1 ? false : true,
    })

    setReviews(reviews)
    setPageReviews(pagination.page)
    setTotalReviews(pagination.total)

    setLoadingReviews(false)
  }

  const loadMoreReviews = async () => {
    setLoadingReviews(true)

    const {
      reviews,
      pagination,
    } = await ProfessionalsService.listProfessionalReviews({
      professionalUUID: professional.uuid,
      page: pageReviews + 1,
      viewAll: selectedReviewOption.id === 1 ? false : true,
    })

    setReviews((prevState) => [...prevState, ...reviews])
    setPageReviews(pagination.page)
    setTotalReviews(pagination.total)
    setLoadingReviews(false)
  }

  const renderBreadcrumb = () => {
    const profileCode = professional?.person_profile[0].profile_code

    return (
      <div className='hasLeftMarginMobile'>
        <Breadcrumb
          steps={[
            {
              href: () =>
                (window.location.href =
                  process.env.REACT_APP_INSTITUTIONAL_URL),
              title: 'Início',
            },
            {
              href: () =>
                profileCode === 'TVTVPSIQ'
                  ? history.push(routes.HOME_PSIQ)
                  : history.push(routes.HOME_PSIC),
              title: `encontrar ${
                profileCode === 'TVTVPSIQ' ? 'psiquiatra' : 'psicólogo'
              }`,
            },
            {
              href: () =>
                routes.PROFESSIONAL_PSIC_PROFILE(
                  this.props.match.params.friendlyUrl
                ),
              title: 'perfil',
            },
          ]}
        />
      </div>
    )
  }

  const recurrenceReservation = reservations?.find(reservation => reservation.recurrence === ReservationRecurrence.WEEKLY)

  const allowsAutomaticSchedulingThroughReservation = useAllowsAutomaticSchedulingThroughReservation()
  const hasReservationWithAutomaticScheduling = !!recurrenceReservation && allowsAutomaticSchedulingThroughReservation
  const shouldShowAutomaticReservationInfo =
    isEnterprise && hasReservationWithAutomaticScheduling && availableCredits === 0 && !expired

  function getMaxSchedules() {
    if (isPsychiatrist && !isParticularPlan) {
      return 1
    }

    if (isCorporate) {
      return maxSchedules
    }

    if (isEnterprise) {
      return availableCredits
    }

    return null
  }

  function getFrequency() {
    if (isEnterprise && recurrenceReservation) {
      return FREQUENCY_OF_ENTERPRISE_WITH_RESERVATION
    }

    return frequency
  }



  const renderProfessionalInfo = () => {
    const image = professional?.person_general.picture_url
    const firstName = professional?.person_general.first_name
    const lastName = professional?.person_general.last_name
    const bio = professional?.person_general.bio
    const docType = professional?.person_profile[0].documents[0].type_name
    const docNumber = professional?.person_profile[0].documents[0].value
    const profileCode = professional?.person_profile[0].profile_code
    const product = professional?.person_profile[0].products[0]

    return (
      <ProfessionalInfo
        image={image}
        name={`${firstName} ${lastName}`}
        documentNumber={`${docType} ${docNumber}`}
        type={profileCode}
        product={product}
        description={bio}
        rating={professional.total_reviews}
        refReviewsEl={refReviewsEl.current}
      />
    )
  }

  const renderProfessionalSchedule = () => {
    return (
      <>
        <div className='ProfessionalProfile__Schedule'>
          <Row direction='column' align='center'>
            <Typography variant='header4' color='primary' weight='bold'>
              Agende sua consulta
            </Typography>
            <Typography variant='content2'>online por vídeo</Typography>
          </Row>
          <LineBreaker
            text={isPsychiatrist && !isParticularPlan ? 'escolha um dia' : 'escolha um ou mais dias'}
            top='15px'
            bottom='15px'
          />
          <ScheduleContainer
            loading={loadingAvailabilities}
            loadingSubmit={loadingSubmit}
            isAdminArea={false}
            fromMonth={fromMonth}
            isPatientAlreadyHasRequiredInfo={isPatientAlreadyHasRequiredInfo}
            isUserAuthenticated={isAuth}
            cleanSelectedDaysOnSubmit={false}
            handleOpenPatientInfoModal={handleOpenPatientInfoModal}
            toMonth={toMonth}
            availabilities={availabilities}
            schedules={schedules}
            maxSchedules={getMaxSchedules()}
            frequency={getFrequency()}
            reservationDate={recurrenceReservation && recurrenceReservation?.startDate}
            showAutomaticReservationInfo={shouldShowAutomaticReservationInfo}
            reservationExceptions={
              recurrenceReservation && recurrenceReservation?.recurrencyExceptions
            }
            error={error}
            onSubmit={handleCreateNewSchedule}
            enableNewPatients={
              professional ? professional.enable_new_patients : false
            }
            onMonthChange={onMonthChange}
            onOpenUpdatePlanModal={() => setShowUpdateDefaultPlanModal(true)}
            isParticular={isParticularPlan}
            isPsychiatrist={isPsychiatrist}
          />
        </div>
        <SocialLinks forMobile />
      </>
    )
  }

  const renderProfessionalBio = () => {
    const bio = professional?.person_general.bio

    return (
      <>
        <LineBreaker text='sobre mim' bottom='20px' align='left' />
        {hasVideo && (
          <div className={videoClasses} ref={videoDiv}>
            {showLayer && (
              <div
                className='ProfessionalProfile__video__layer'
                onClick={() => {
                  setPlayVideo(true)
                  setShowLayer(false)
                }}
              >
                <div className='ProfessionalProfile__video__layer__button'>
                  <CoreIcons
                    name='ArrowBack'
                    fill='#ffffff'
                    height='40px'
                    width='40px'
                  />
                </div>
                <Typography customClassName='ProfessionalProfile__video__layer__typography'>
                  Assistir novamente
                </Typography>
              </div>
            )}
            <div className='__wrapper'>
              <ReactPlayer
                id='pip'
                onEnded={() => {
                  setShowLayer(true)
                  setPlayVideo(false)
                }}
                controls
                className='react-player'
                url={`${hasVideo}?html5=1`}
                width='500'
                height='auto'
                playing={playVideo}
              />
            </div>
          </div>
        )}
        <TextContainer text={bio} length={370} class='' />
      </>
    )
  }

  const handleProfessionalInfo = () => {
    const skills = professional?.person_profile[0]?.skills?.map(
      (skill) => skill.skill_name
    )
    const therapyApproaches = therapyApproachesList?.map(({ name }) => name)
    const skillsThatAreTherapyApproaches = skills?.filter((skill) =>
      therapyApproaches.includes(skill)
    )
    const skillsWithoutTherapyApproaches = skills?.filter(
      (skill) => !therapyApproaches.includes(skill)
    )

    return {
      skills,
      therapyApproaches,
      skillsThatAreTherapyApproaches,
      skillsWithoutTherapyApproaches,
    }
  }

  const renderPsychologistSkills = () => {
    return (
      <>
        <LineBreaker
          text='experiências'
          top='40px'
          bottom='20px'
          align='left'
        />
        <ProfileSpecialities
          specialities={
            handleProfessionalInfo()?.skillsWithoutTherapyApproaches
          }
        />
      </>
    )
  }

  const renderProfessionalTherapyApproaches = () => {
    return (
      <>
        <div className='ProfessionalProfile__TherapyApproachWrapper'>
          <LineBreaker text='abordagens terapêuticas' top='40px' align='left' />
          <Typography
            variant='content2'
            onClick={() => setShowTherapyApproachModal(true)}
            color='primary'
            weight='bold'
            customClassName='ProfessionalProfile__TherapyApproachWrapper__OpenModal'
          >
            O que é?
          </Typography>
        </div>
        <ProfileSpecialities
          specialities={
            handleProfessionalInfo()?.skillsThatAreTherapyApproaches
          }
        />
      </>
    )
  }

  const renderProfessionalMeetsAges = () => {
    const iconsMap = {
      'IDOSO': ElderlysIcon,
      'ADULTO': AdultsIcon,
      'ADOLESCENTE': AdolescentsIcon,
      'CRIANÇA': KidsIcon,
    }

    function renderRangeAge(startAge, endAge) {
      const ElderlysStartAge = 60

      if (startAge === endAge) return startAge
      if (startAge === ElderlysStartAge) return '60+'

      return `${startAge} a ${endAge}`
    }

    const orderedAgeRanges = ageRanges?.sort((a, b) => {
      if (a.start_age < b.start_age) return -1
      if (a.start_age > b.start_age) return 1
      return 0
    })

    return (
      <>
        <LineBreaker text='atende as idades' top='50px' align='left' />
        <ul className='ProfessionalProfile__MeetAgesWrapper'>
          {orderedAgeRanges.map(range => {
            const Icon = iconsMap[range.category]

            return (
              <li key={range.id}>
                <Icon />
                <Typography
                  variant='content1'
                  weight='bold'
                >
                  {renderRangeAge(range.start_age, range.end_age)}
                </Typography>
                <Typography
                  variant='content2'
                  inlineStyles={{ textTransform: 'capitalize' }}
                >
                  {`${range.category.toLowerCase()}s`}
                </Typography>
              </li>
            )
          })}
        </ul>
      </>
    )
  }

  const flagsMap = {
    'Português': { src: PortugueseFlagImage, alt: 'bandeira do Brasil' },
    'Alemão': { src: GermanFlagImage, alt: 'bandeira da Alemanha' },
    'Inglês': { src:EnglishFlagImage, alt: 'bandeira dos Estados Unidos' },
    'Espanhol': { src: SpanishFlagImage, alt: 'bandeira da Espanha' },
  }

  const renderProfessionalMeetsLanguages = () => {
    const portugueseLanguage = languages.find(language => language.name === 'Português')
    const orderedLanguages = languages.sort((a, b) => {
      if (a.name < b.name) {
        return -1
      }
      if (a.name > b.name) {
        return 1
      }
      return 0
    })
    const newLanguages = orderedLanguages.filter(language => language.name !== portugueseLanguage.name)
    newLanguages.unshift(portugueseLanguage)

    return (
      <>
        <LineBreaker text='idiomas de atendimento' top='40px' align='left' />
        <ul className='ProfessionalProfile__MeetsLanguagesWrapper'>
          {newLanguages.map(language => {
            const flag = flagsMap[language.name]

            return (
              <li key={language.id}>
                <div>
                  {language.name === 'Libras'
                    ? <SignLanguageFlag />
                    : <img width={46} src={flag.src} alt={flag.alt}/>
                  }
                  <Typography
                    variant='content2'
                  >
                    {language.name}
                  </Typography>
                </div>
              </li>
            )
          })}
        </ul>
      </>
    )
  }

  const renderPsychiatristSkills = () => {
    let subspecialties = professional?.person_profile[0].subspecialties.map(
      (sub) => sub.subspecialty_name
    )
    let themes = professional?.person_profile[0].skills.map(
      (skill) => skill.skill_name
    )

    return (
      <>
        <LineBreaker
          text='SUBESPECIALIDADES'
          top='40px'
          bottom='20px'
          align='left'
        />
        <ProfileSpecialities specialities={subspecialties} />
        <LineBreaker
          text='experiências'
          top='40px'
          bottom='20px'
          align='left'
        />
        <ProfileSpecialities specialities={themes} />
      </>
    )
  }

  const renderProfessionalEducation = () => {
    const educations = professional?.person_profile[0].education.map((ed) => {
      return {
        school: ed.institution,
        course: ed.course_name,
        startYear: ed.start_year,
        endYear: ed.end_year,
      }
    })

    return (
      <>
        <LineBreaker text='formação' top='50px' bottom='20px' align='left' />
        <ProfileEducation educations={educations} />
      </>
    )
  }

  const renderCreditsPanel = () => {
    if (!hideCreditsPanel && isPsychiatrist && !showPsychiatry) return

    if (hideCreditsPanel) return

    return (
      <CreditsPanel
        isEnterprise={isEnterprise}
        isCorporate={isCorporate}
        isPsiqProfile={isPsychiatrist}
        professionalProfileRole={profileRole}
        isProfessionalProfile={isPsychologist || isPsychiatrist}
        isCareplus={isCareplus}
        isCassi={isCassi}
        isCapitation={isCapitation}
        hasButton={hasButton}
        setHasButton={setHasButton}
        planGroupName={planGroupName}
        creditsStatus={creditsStatus}
        availableCredits={availableCredits}
        maxCredits={maxCredits}
        enableDetails={enableDetails}
        validDate={validDate}
        expired={expired}
        errorButton={errorButton}
        loadingButton={loadingButton}
        solicitationDate={solicitationDate}
        isUnlimited={isUnlimited}
        startDate={startDate}
        corporateEligibility={corporateEligibility}
        loading={loadingEligibility}
        forceUpdate={forceUpdate}
        onClickRequestCreditsButton={onClickRequestCreditsButton}
        psychiatryEligibility={psychiatryEligibility}
        psychiatryEligibilityLoading={psychiatryEligibilityLoading}
        onRequestTreatement={handleRequestTreatementStatus}
        planGroupCode={planGroupCode}
        reasonCode={reasonCode}
        planCode={planCode}
        creditsPanelErrorMessage={creditsPanelErrorMessage}
        username={username}
        patientPlans={patientPlansEligibility}
      />
    )
  }

  if (loading) {
    return (
      <div className='ProfessionalProfile__loading'>
        <Spinner />
      </div>
    )
  }

  const renderProfessionalReviews = () => {
    const total = professional.total_reviews
    if (total === 0) return

    return (
      <>
        <LineBreaker
          text='avaliações dos pacientes'
          top='50px'
          bottom='20px'
          align='left'
        />
        <div className='ProfessionalProfile__Reviews' ref={refReviewsEl}>
          <div className='ProfessionalProfile__Reviews__Header'>
            <div className='ProfessionalProfile__Reviews__Header__Texts'>
              <Typography
                variant='content1'
                weight='bolder'
                inlineStyles={{
                  color: '#FCBC55',
                }}
              >
                {`${total} ${
                  total === 1 ? 'avaliação' : 'avaliações'
                } com nota máxima`}
              </Typography>
              <Typography
                italic
                variant={isMobile ? 'content2' : 'content3'}
                inlineStyles={{
                  color: '#898989',
                  maxWidth: isMobile ? 'initial' : '290px',
                  lineHeight: isMobile ? '19px' : '16px',
                  padding: '5px 0',
                }}
              >
                *Abreviamos os nomes para preservar a privacidade dos nossos
                pacientes.
              </Typography>
            </div>
            <Select
              customClassName='ProfessionalProfile__Reviews__Header__Select'
              label=''
              options={reviewsOptions}
              onSelect={(reviewOption) =>
                handleSelectReviewOption(reviewOption.id)
              }
              selected={selectedReviewOption}
            />
          </div>
          {!reviews.length && !loadingReviews && (
            <div className='ProfessionalProfile__Reviews__EmptyReviews'>
              <BallonCommentIcon
                fill='#c1c1c1'
                height='52'
                width='52'
                viewBox='0 0 52 52'
              />
              <Typography
                variant='content2'
                inlineStyles={{ color: '#c1c1c1' }}
              >
                Nenhum depoimento recebido
              </Typography>
            </div>
          )}
          <ListContainer>
            {reviews.map((review) => (
              <Review
                rating
                key={review.id}
                reviewId={review.id}
                author={review.author}
                createdAt={format(review.createdAt, 'LLLL yyyy', {
                  locale: pt,
                })}
                review={review.review}
              />
            ))}
          </ListContainer>
          {loadingReviews && (
            <>
              <ReviewLoading isMobile={isMobile} />
              <ReviewLoading isMobile={isMobile} />
              <ReviewLoading isMobile={isMobile} />
              <ReviewLoading isMobile={isMobile} />
              <ReviewLoading isMobile={isMobile} />
            </>
          )}
          {totalReviews > reviews.length && (
            <div className='ProfessionalProfile__Reviews__Button'>
              <Button
                variant='contained'
                inlineStyles={{
                  height: '36px',
                  padding: '0 20px',
                }}
                onClick={loadMoreReviews}
              >
                ver mais avaliações
              </Button>
            </div>
          )}
        </div>
      </>
    )
  }

  if (loading) {
    return (
      <div className='ProfessionalProfile__loading'>
        <Spinner />
      </div>
    )
  }

  return (
    <>
      <Container isMobileFull>
        {!isMobile && (
          <Row align='space-between' justify='space-between'>
            <Column>{renderBreadcrumb()}</Column>
          </Row>
        )}

        <Row className='ProfessionalProfile' direction='row'>
          <Column className='ProfessionalProfile__Info' colWidth={8}>
            <Column>{renderProfessionalInfo()}</Column>
            <Container>
              {isMobile && (
                <Column leftGutter colWidth={8}>
                  {renderProfessionalSchedule()}
                </Column>
              )}

              <Row margin='3rem 0 0 0'>
                <Column>{renderProfessionalBio()}</Column>
              </Row>

              <Row>
                <Column>
                  {isPsychiatrist
                    ? renderPsychiatristSkills()
                    : renderPsychologistSkills()}
                </Column>
              </Row>

              {isPsychiatrist ? null : (
                <Row>
                  <Column>{renderProfessionalTherapyApproaches()}</Column>
                </Row>
              )}

              {ageRanges.length > 0 && (
                <Row>
                  <Column>{renderProfessionalMeetsAges()}</Column>
                </Row>
              )}

              <Row>
                <Column>{renderProfessionalEducation()}</Column>
              </Row>

              <Row>
                <Column>{renderProfessionalMeetsLanguages()}</Column>
              </Row>

              <Row>
                <Column>{renderProfessionalReviews()}</Column>
              </Row>
            </Container>
          </Column>
          {!isMobile && (
            <Column leftGutter colWidth={4}>
              {renderProfessionalSchedule()}
            </Column>
          )}
        </Row>
      </Container>

      <Footer isMobile={isMobile} />
      {renderCreditsPanel()}
      {showRequiredPatientInfoModal ? (
        <RequiredPatientInfoModal
          onClose={handleCloseRequiredPatientInfoModal}
          isFormFulfillmentObligatory
          title='Para continuar agendando, é preciso completar seu cadastro'
          patientInfo={{
            cpf: userDataAuthenticated?.cpf,
            dateBirth: userDataAuthenticated?.dateBirth,
            gender: userDataAuthenticated?.gender,
          }}
        />
      ) : null}

      {showTherapyApproachModal ? (
        <TherapyApproachModal
          onClose={() => setShowTherapyApproachModal(false)}
        />
      ) : null}
      {showUpdateDefaultPlanModal && (
        <UpdateDefaultPlanModal
          onClose={() => setShowUpdateDefaultPlanModal(false)}
          professionalFullName={`${professional?.person_general.first_name} ${professional?.person_general.last_name}`}
          professionalDoc={`${professional?.person_profile[0].documents[0].type_name} ${professional?.person_profile[0].documents[0].value}`}
          professionalType={professional?.person_profile[0].profile_code}
          professionalImage={professional?.person_general.picture_url}
          onSelectPlan={handleUpdateDefaultPlan}
          patientPlans={orderedPatientPlans}
        />
      )}
    </>
  )
}

export default ProfessionalProfile
